import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getInstalledApps, type InstalledApp } from '../miniApps';

export function HomePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [apps, setApps] = useState<InstalledApp[]>([]);

  const loadApps = useCallback(() => {
    setApps(getInstalledApps());
  }, []);

  useEffect(() => {
    loadApps();
    window.addEventListener('focus', loadApps);
    return () => window.removeEventListener('focus', loadApps);
  }, [loadApps]);

  return (
    <div className="mx-auto max-w-4xl p-6 space-y-6">
      <div className="flex items-center gap-3 rounded-xl bg-white p-4 shadow-sm">
        <span className="text-3xl">📱</span>
        <h1 className="text-2xl font-bold text-gray-900">{t('home.title')}</h1>
      </div>

      <div className="rounded-xl bg-white p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">{t('home.apps')}</h2>
        <p className="text-sm text-gray-500 mt-1">
          {t('home.appsInstalled', { count: apps.length })}
        </p>

        {apps.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-gray-400">
            <span className="text-5xl mb-3">📦</span>
            <p className="text-sm">{t('home.noApps')}</p>
            <button
              onClick={() => navigate('/store')}
              className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              {t('home.openStore')}
            </button>
          </div>
        ) : (
          <div className="mt-4 divide-y divide-gray-100">
            {apps.map(app => (
              <button
                key={app.app_id}
                onClick={() => navigate(`/app/${app.app_id}`)}
                className="flex w-full items-center gap-4 py-3 px-2 text-left hover:bg-gray-50 rounded-lg transition-colors"
              >
                <span className="text-2xl">{iconEmoji(app.icon)}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900 truncate">{app.name}</p>
                  {app.description && (
                    <p className="text-sm text-gray-500 truncate">{app.description}</p>
                  )}
                </div>
                <span className="text-gray-400">›</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function iconEmoji(icon: string | null): string {
  const map: Record<string, string> = {
    smile: '😊',
    camera: '📷',
    qrcode: '📱',
    video: '📹',
    puzzle: '🧩',
  };
  return map[icon ?? ''] ?? '🧩';
}
